// Comprehensive error handling for API responses
export interface ApiErrorResponse {
  field_errors?: {
    [fieldKey: string]:
      | string[]
      | {
          [additionalProp: string]: string;
        };
  };
  non_field_errors?: string[];
  detail?: string;
  message?: string;
  // Direct field errors (e.g., { "country_code": ["error message"] })
  [fieldKey: string]: string | string[] | object | undefined;
}

export interface ProcessedErrors {
  fieldErrors: Record<string, string>;
  nonFieldErrors: string[];
  generalError?: string;
}

/**
 * Translates common backend error messages to their localized equivalents
 */
export const translateErrorMessage = (message: string, t?: (key: string) => string): string => {
  if (!t) return message;

  // Common Django/DRF error messages
  const errorTranslations: Record<string, string> = {
    'This field is required.': t('validation.fieldRequired'),
    'This field may not be blank.': t('validation.fieldNotBlank'),
    'This field may not be null.': t('validation.fieldNotNull'),
    'Ensure this field has no more than': t('validation.maxLength'),
    'Ensure this field has at least': t('validation.minLength'),
    'Enter a valid email address.': t('validation.invalidEmail'),
    'Enter a valid URL.': t('validation.invalidUrl'),
    'A valid integer is required.': t('validation.invalidInteger'),
    'A valid number is required.': t('validation.invalidNumber'),
  };

  // Check for exact matches first
  if (errorTranslations[message]) {
    return errorTranslations[message];
  }

  // Check for partial matches (e.g., "Ensure this field has no more than 100 characters")
  for (const [englishError, translationKey] of Object.entries(errorTranslations)) {
    if (message.startsWith(englishError)) {
      // For messages with numbers, return the translated key with the original message
      const match = message.match(/\d+/);
      if (match && (translationKey.includes('max') || translationKey.includes('min'))) {
        // Return translated message with the number from original message
        return translationKey.replace('{count}', match[0]);
      }
      return translationKey;
    }
  }

  return message;
};

/**
 * Processes API error responses and converts them to a consistent format
 * Handles all possible error structures from the backend
 */
export const processApiErrors = (
  errorResponse: ApiErrorResponse,
  t?: (key: string) => string
): ProcessedErrors => {
  const fieldErrors: Record<string, string> = {};
  const nonFieldErrors: string[] = [];

  // Process field_errors
  if (errorResponse.field_errors) {
    Object.entries(errorResponse.field_errors).forEach(([fieldKey, errors]) => {
      if (Array.isArray(errors)) {
        // Handle string[] format - translate each error message
        const translatedErrors = errors.map(err => translateErrorMessage(err, t));
        fieldErrors[fieldKey] = translatedErrors.join(', ');
      } else if (typeof errors === 'object' && errors !== null) {
        // Handle nested object format
        const nestedErrors = Object.values(errors).map(err =>
          typeof err === 'string' ? translateErrorMessage(err, t) : err
        );
        fieldErrors[fieldKey] = nestedErrors.join(', ');
      }
    });
  }

  // Process direct field errors (e.g., { "country_code": ["error"] })
  Object.entries(errorResponse).forEach(([key, value]) => {
    // Skip known keys that are not direct field errors
    if (
      key === 'field_errors' ||
      key === 'non_field_errors' ||
      key === 'detail' ||
      key === 'message'
    ) {
      return;
    }

    // If the value is an array of strings, treat it as a field error
    if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
      const translatedErrors = value.map(err => translateErrorMessage(err, t));
      fieldErrors[key] = translatedErrors.join(', ');
    }
  });

  // Process non_field_errors
  if (errorResponse.non_field_errors) {
    nonFieldErrors.push(...errorResponse.non_field_errors);
  }

  // Process detail or message as general error
  const generalError = errorResponse.detail || errorResponse.message;

  return {
    fieldErrors,
    nonFieldErrors,
    generalError,
  };
};

/**
 * Converts field keys to user-friendly display names
 */
export const getFieldDisplayName = (fieldKey: string): string => {
  return fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Creates user-friendly error messages
 */
export const createErrorMessage = (fieldKey: string, errorMessage: string): string => {
  const fieldName = getFieldDisplayName(fieldKey);
  return `${fieldName}: ${errorMessage}`;
};

/**
 * Handles API error responses and returns processed errors
 */
export const handleApiError = async (
  response: Response,
  t?: (key: string) => string
): Promise<ProcessedErrors> => {
  try {
    const errorData: ApiErrorResponse = await response.json();
    return processApiErrors(errorData, t);
  } catch (parseError) {
    // If JSON parsing fails, return a generic error
    return {
      fieldErrors: {},
      nonFieldErrors: [],
      generalError: t
        ? t('validation.requestFailed')
        : `Request failed with status ${response.status}: ${response.statusText}`,
    };
  }
};
